import * as React from 'react';
import { useEffect, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import BasicLayout from 'components/layout/BasicLayout';

import { PageWrapper } from 'components/common';
import { TabsProps } from 'antd/lib/tabs';
import { Sticky, StickyContainer } from 'react-sticky';
import { Icon, message, Tabs } from 'antd';
import {
    apiGetCategory,
    apiGetCategoryGroups,
    apiGetCategoryKeyFigureAggregations
} from 'api/categories';
import {
    AggregationKeyFigures,
    Category,
    Group,
    GroupInfo,
    GroupsByCategories,
    KeyFigureData,
    PageResponse,
    BasicKeyFigureInfo
} from 'components/context/types';
import { prepareAggregationsToKeyFigureData } from 'utils/keyfigures';
import t from 'utils/translations';

import {
    apiGetLatestGroupKeyFigures,
    apiRemoveCompanyFromGroup
} from 'api/groups';
import { GroupTableInfo } from 'components/CommonGroupAndCategoryStuff/GroupsTableHelpers';
import BreadcrumbsTitle from 'components/CommonGroupAndCategoryStuff/BreadcrumbsTitle';
import styled from 'styled-components';
import theme from 'theme';
import CategoryTabContent from 'components/pages/categorypage/CategoryTabContent';
import GroupTabContent from 'components/pages/categorypage/GroupTabContent';
import CategoryActionMenu from 'components/pages/grouplistpage/actionmenu/CategoryActionMenu';
import GroupActionMenu from 'components/pages/grouplistpage/actionmenu/GroupActionMenu';
import { AddButton } from 'components/common/modal/ModalCommon';
import {
    CompanyTablePagination,
    CompanyTableSortField
} from 'components/CommonGroupAndCategoryStuff/CompanyTable';

interface RouteInfo {
    categoryId: string;
    groupId?: string;
}

const StyledTabs = styled(Tabs)`
    .ant-tabs-bar {
        background-color: ${theme.colors.gray_light} !important;
    }
`;

const TitleWrapper = styled.div`
    display: flex;
    justify-content: space-between;
`;

export const SettingsMenu = styled.ul`
    list-style: none;
    margin-bottom: 0;

    li {
        cursor: pointer;
        padding: 6px 0;
    }

    li:hover {
        text-decoration: underline;
    }
`;

function renderTabBar(
    tabsProps: TabsProps,
    DefaultTabBar: typeof React.Component
) {
    return (
        <Sticky bottomOffset={80}>
            {({ style }) => (
                <DefaultTabBar
                    {...tabsProps}
                    style={{ ...style, zIndex: 1, background: '#fff' }}
                />
            )}
        </Sticky>
    );
}

function CategoryPage(props: RouteComponentProps<RouteInfo>) {
    const [category, setCategory] = useState<Category | undefined>(undefined);

    const [categoryTabGraphData, setCategoryTabGraphData] = useState<
        KeyFigureData
    >({
        seriesName: '',
        keyFigures: []
    });

    const [categoryTableData, setCategoryTableData] = useState<
        GroupTableInfo[]
    >([]);
    const [selectedGroupInfo, setSelectedGroupInfo] = useState<
        GroupTableInfo | undefined
    >(undefined);

    const [activeTab, setActiveTab] = useState<string>(
        props.match.params.groupId ? props.match.params.groupId : 'kaikki'
    );

    const [categoryTablePagination, setCategoryTablePagination] = useState({
        currentPage: 1,
        pageSize: 50,
        sortField: CompanyTableSortField.Name,
        sortOrder: 'ASC'
    } as CompanyTablePagination);

    const [isGraphDataLoading, setIsGraphDataLoading] = useState(true);
    const [isTableDataLoading, setIsTableDataLoading] = useState(true);
    const [reloadGroup, setReloadGroup] = useState(true);
    const [
        amountCompaniesInCurrentGroup,
        setAmountCompaniesInCurrentGroup
    ] = useState(0);

    useEffect(() => {
        fetchCategory();
    }, [props.match.params.categoryId]);

    useEffect(() => {
        updateCategoryTabTableData();
        updateCategoryTabGraphData();
    }, [category]);

    useEffect(() => {
        updateCategoryTableChildren();
    }, [categoryTablePagination]);

    function afterGroupActions() {
        setReloadGroup(!reloadGroup);
        updateCategoryTabTableData();
    }

    function fetchCategory() {
        const categoryId = props.match.params.categoryId;

        apiGetCategory(categoryId)
            .then(res => res.json())
            .then((data: Category) => setCategory(data));
    }

    const updateCategoryTabGraphData = () => {
        if (category) {
            setIsGraphDataLoading(true);
            apiGetCategoryKeyFigureAggregations(category.categoryId)
                .then(res => res.json())
                .then((data: PageResponse<AggregationKeyFigures>) => {
                    setCategoryTabGraphData(
                        prepareAggregationsToKeyFigureData(
                            data.content,
                            category.categoryName
                        )
                    );
                })
                .finally(() => setIsGraphDataLoading(false));
        }
    };

    const updateCategoryTabTableData = () => {
        if (category) {
            setIsTableDataLoading(true);
            apiGetCategoryGroups(category.categoryId)
                .then(res => res.json())
                .then((data: PageResponse<GroupInfo>) => {
                    const groupData = data.content.map(groupInfo => ({
                        groupId: groupInfo.group.groupId,
                        groupName: groupInfo.group.groupName,
                        numberOfCompanies: groupInfo.numberOfCompanies
                    }));

                    setCategoryTableData(groupData);

                    if (props.match.params.groupId) {
                        const selectedGroup = groupData.find(
                            groupInfo =>
                                groupInfo.groupId === props.match.params.groupId
                        );
                        setSelectedGroupInfo(selectedGroup);
                    }
                })
                .finally(() => setIsTableDataLoading(false));
        }
    };

    function deleteCompanyFromGroup(groupId: string, businessId: string) {
        apiRemoveCompanyFromGroup(groupId, businessId)
            .then(res => {
                if (res.status === 200) {
                    message.success(t('DELETE_BUSINESS_SUCCESS'));
                    const groupIndex = categoryTableData.findIndex(
                        (group: GroupTableInfo) => group.groupId === groupId
                    );
                    fetchCategoryTableChildren(groupId, groupIndex);
                } else {
                    message.error(t('DELETE_BUSINESS_FAIL'));
                }
            })
            .catch(() => {
                message.error(t('DELETE_BUSINESS_FAIL'));
            });
    }

    function expandRow(expanded: any, record: any) {
        const groupId = record.key;

        if (expanded) {
            const groupIndex = categoryTableData.findIndex(
                (group: GroupTableInfo) => group.groupId === groupId
            );
            if (categoryTableData[groupIndex].companies === undefined) {
                fetchCategoryTableChildren(groupId, groupIndex);
            }
        }
    }

    function onTabChange(activeKey: string) {
        setActiveTab(activeKey);
        if (!props.match.params.groupId) {
            props.history.push(`${props.match.url}/${activeKey}`);
        } else {
            props.history.push(`${activeKey}`);
        }

        if (activeKey === 'kaikki') {
            setSelectedGroupInfo(undefined);
        } else {
            const selectedGroup = categoryTableData.find(
                groupInfo => groupInfo.groupId === activeKey
            );
            setAmountCompaniesInCurrentGroup(
                selectedGroup ? selectedGroup.numberOfCompanies : 0
            );
            setSelectedGroupInfo(selectedGroup);
        }
    }

    function updateCategoryTableChildren() {
        categoryTableData.forEach((groupTableInfo, idx) => {
            if (groupTableInfo.companies !== undefined) {
                fetchCategoryTableChildren(groupTableInfo.groupId, idx);
            }
        });
    }

    function fetchCategoryTableChildren(groupId: string, groupIndex: number) {
        setIsTableDataLoading(true);
        apiGetLatestGroupKeyFigures(
            groupId,
            categoryTablePagination.currentPage,
            categoryTablePagination.pageSize,
            categoryTablePagination.sortField,
            categoryTablePagination.sortOrder
        )
            .then((data: PageResponse<BasicKeyFigureInfo>) => {
                const groupsDataTmp = [...categoryTableData];
                groupsDataTmp[groupIndex].companies = data.content;
                setCategoryTableData(groupsDataTmp);
            })
            .finally(() => setIsTableDataLoading(false));
    }

    const groupsByCategory = {
        categoryId: category ? category.categoryId : '',
        categoryName: category ? category.categoryName : '',
        groups: categoryTableData.map(
            group =>
                ({
                    groupId: group.groupId,
                    groupName: group.groupName
                } as Group)
        )
    } as GroupsByCategories;

    const actionMenuActivatingElement = (
        <AddButton type="primary" size="default">
            <Icon type="setting" /> {t('EDIT_GROUP')}
        </AddButton>
    );

    return (
        <BasicLayout>
            <PageWrapper>
                <TitleWrapper>
                    <BreadcrumbsTitle
                        breadcrumbItems={[
                            { text: t('GROUPS_TITLE'), linkTo: '/ryhmat' },
                            ...(category
                                ? [
                                      {
                                          text: category.categoryName,
                                          onClickFunction: () => {
                                              setActiveTab('kaikki');
                                              setSelectedGroupInfo(undefined);
                                          }
                                      }
                                  ]
                                : []),
                            ...(selectedGroupInfo
                                ? [{ text: selectedGroupInfo.groupName }]
                                : [])
                        ]}
                    />
                    {!selectedGroupInfo && (
                        <CategoryActionMenu
                            afterActionsFunction={fetchCategory}
                            groupsByCategories={[groupsByCategory]}
                            id={category ? category.categoryId : ''}
                            name={category ? category.categoryName : ''}
                            activatingElement={actionMenuActivatingElement}
                            numberOfGroupsInCategory={categoryTableData.length}
                            navigateAfterDeletion={() => {
                                window.location.href = '/ryhmat';
                            }}
                        />
                    )}
                    {selectedGroupInfo && (
                        <GroupActionMenu
                            afterActionsFunction={afterGroupActions}
                            groupsByCategories={[groupsByCategory]}
                            id={selectedGroupInfo.groupId}
                            name={selectedGroupInfo.groupName}
                            activatingElement={actionMenuActivatingElement}
                            numberOfCompaniesInGroup={
                                amountCompaniesInCurrentGroup
                            }
                            navigateAfterDeletion={() => {
                                setActiveTab('kaikki');
                            }}
                        />
                    )}
                </TitleWrapper>
                <StickyContainer>
                    <StyledTabs
                        activeKey={activeTab}
                        onChange={onTabChange}
                        animated={false}
                        renderTabBar={renderTabBar}
                    >
                        <Tabs.TabPane tab={'Kaikki yritykset'} key={'kaikki'}>
                            <CategoryTabContent
                                companyTablePagination={categoryTablePagination}
                                setCompanyTablePagination={
                                    setCategoryTablePagination
                                }
                                deleteCompanyFromGroup={deleteCompanyFromGroup}
                                expandRow={expandRow}
                                fetchCategory={fetchCategory}
                                graphData={categoryTabGraphData}
                                groupsByCategory={groupsByCategory}
                                groupsTableData={categoryTableData}
                                isGraphDataLoading={isGraphDataLoading}
                                isTableDataLoading={isTableDataLoading}
                                setActiveTab={onTabChange}
                            />
                        </Tabs.TabPane>

                        {categoryTableData.map((groupInfo: GroupTableInfo) => {
                            const groupId = groupInfo.groupId;
                            const groupName = groupInfo.groupName;
                            return (
                                <Tabs.TabPane tab={groupName} key={groupId}>
                                    <GroupTabContent
                                        groupId={groupId}
                                        groupName={groupName}
                                        reload={reloadGroup}
                                    />
                                </Tabs.TabPane>
                            );
                        })}
                    </StyledTabs>
                </StickyContainer>
            </PageWrapper>
        </BasicLayout>
    );
}

export default withRouter(CategoryPage);
